// src/store/slices/menuItemsSlice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Updated interface to match the individual day schedule structure
export interface MealDaySchedule {
  day: string;
  startsAt: string;
  endsAt: string;
}

export interface MealTimeInfo {
  mappingId: string;
  mealTimeId: string;
  mealTimeName: string;
  mealType: string;
  description?: string; // <-- Added description here
  startsAt: string | null;
  endsAt: string | null;
  days?: MealDaySchedule[];
  isActive: boolean;
  isExpired: boolean;
  price: number | null;
}

export interface MenuItem {
  pk_id: string;
  restaurant_id: string;
  sku_id: string;
  name: string;
  category: string;
  description: string;
  price:
    | {
        dining: number;
        online: number;
      }
    | string;
  large_price: string;
  indining_price: string;
  indining_large_price: string;
  image: string;
  is_enabled: boolean;
  is_item_verified: boolean;
  product_description: string;
  level1_category: string;
  level2_category: string;
  ingredients: string[];
  allergens: string[];
  food_type: string;
  is_vegan: string;
  modifiers_list: any[];
  party_orders: any[];
  builder_type?: string;
  inventoryStatus?: boolean;
  modifiers?: string[];
  posReferenceId?: string;
  mealTimes?: MealTimeInfo[];

  // Clean UI Card Properties
  MealName?: string|null;
  mealTimeRange?: string| null; // e.g. "10:00 - 18:00" or AM/PM Date Range
  isMealTimeActive?: boolean;
  isMealTimeExpired?: boolean;
  
  [key: string]: any;
}

interface MenuItemsState {
  items: MenuItem[];
  mealTimes: MealTimeInfo[];
  loading: boolean;
  error: string | null;
  selectedCategory: string | null;
  selectedMealTime: string | null;
}

const initialState: MenuItemsState = {
  items: [],
  mealTimes: [],
  loading: false,
  error: null,
  selectedCategory: null,
  selectedMealTime: null,
};

export const menuItemsSlice = createSlice({
  name: "menuItems",
  initialState,
  reducers: {
    fetchMenuItemsRequest: (state) => {
      state.loading = true;
      state.error = null;
    },

    fetchMenuItemsSuccess: (state, action: PayloadAction<MenuItem[]>) => {
      state.loading = false;
      state.items = action.payload;
      state.error = null;
    },

    fetchMenuItemsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.items = [];
    },
    
    setMealTimes: (state, action: PayloadAction<MealTimeInfo[]>) => {
      state.mealTimes = action.payload;
    },

    setSelectedMealTime: (state, action: PayloadAction<string | null>) => {
      state.selectedMealTime = action.payload;
    },

    setSelectedCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedCategory = action.payload;
    },

    clearMenuItems: (state) => {
      state.items = [];
      state.mealTimes = [];
      state.loading = false;
      state.error = null;
      state.selectedCategory = null;
      state.selectedMealTime = null;
    },
  },
});

export const {
  fetchMenuItemsRequest,
  fetchMenuItemsSuccess,
  fetchMenuItemsFailure,
  setMealTimes,
  setSelectedMealTime,
  setSelectedCategory,
  clearMenuItems,
} = menuItemsSlice.actions;

export default menuItemsSlice.reducer;